import { endOfDay, isSameDay, startOfDay } from "date-fns";
import { IEvent, ITravelEvent } from "../../interfaces/event";
import { IResource } from "../../interfaces/resource";
import {
    ITravelCoordinate,
    ITravelMeasureRequest,
    ITravelMeasureResultSegment,
    ITravelSegment,
    ResourceTravelLocationType
} from "../../interfaces/travel";

const toFiniteCoordinateValue = (value: number | string | null | undefined): number | null => {
    if (value == null || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const toCoordinateOrNull = (
    value: {
        latitude?: number | string | null;
        longitude?: number | string | null;
    } | null | undefined
): { latitude: number; longitude: number; } | null => {
    const latitude = toFiniteCoordinateValue(value?.latitude);
    const longitude = toFiniteCoordinateValue(value?.longitude);

    if (latitude == null || longitude == null) {
        return null;
    }

    return { latitude, longitude };
};

const coordinatesEqual = (
    left: { latitude: number; longitude: number; } | null,
    right: { latitude: number; longitude: number; } | null
): boolean => {
    if (!left || !right) {
        return false;
    }

    return Math.abs(left.latitude - right.latitude) < 0.0000001 &&
        Math.abs(left.longitude - right.longitude) < 0.0000001;
};

const resolveResourceTravelAnchor = (
    resource: IResource | undefined,
    locationType: ResourceTravelLocationType | null | undefined,
    locationLabel: string | null | undefined,
    siteLocationCoordinates?: ITravelCoordinate | null,
    organizationalUnitFallbackCoordinates?: ITravelCoordinate | null
): { latitude: number; longitude: number; } | null => {
    if (!resource) {
        return null;
    }

    const labelBasedAnchor = resolveResourceTravelAnchorByLabel(
        locationLabel,
        resource,
        siteLocationCoordinates,
        organizationalUnitFallbackCoordinates
    );

    if (labelBasedAnchor) {
        return labelBasedAnchor;
    }

    if (locationType == null) {
        return null;
    }

    switch (locationType) {
        case ResourceTravelLocationType.ResourceAddress:
            return toCoordinateOrNull(resource.resourceAddressCoordinates);
        case ResourceTravelLocationType.OrganizationalUnitAddress:
            return toCoordinateOrNull(siteLocationCoordinates) ?? toCoordinateOrNull(organizationalUnitFallbackCoordinates);
        case ResourceTravelLocationType.LocationAgnostic:
        default:
            return null;
    }
};

const resolveResourceTravelAnchorByLabel = (
    locationLabel: string | null | undefined,
    resource: IResource | undefined,
    siteLocationCoordinates?: ITravelCoordinate | null,
    organizationalUnitFallbackCoordinates?: ITravelCoordinate | null
): { latitude: number; longitude: number; } | null => {
    const normalized = String(locationLabel ?? '').trim().toLowerCase();
    if (!normalized || !resource) {
        return null;
    }

    if (normalized.includes('resource address')) {
        return toCoordinateOrNull(resource.resourceAddressCoordinates);
    }

    if (normalized.includes('organizational unit address') || normalized.includes('organisation unit address')) {
        return toCoordinateOrNull(siteLocationCoordinates) ?? toCoordinateOrNull(organizationalUnitFallbackCoordinates);
    }

    if (normalized.includes('location agnostic')) {
        return null;
    }

    return null;
};

const isAssignedVisibleShift = (shift: IEvent): boolean => {
    const startMs = new Date(shift.start).getTime();
    const endMs = new Date(shift.end).getTime();

    return shift.resourceId !== '0' &&
        !shift.display &&
        shift.availabilityType == null &&
        !Number.isNaN(startMs) &&
        !Number.isNaN(endMs) &&
        endMs > startMs;
};

const RETURN_HOME_GAP_MS = 2 * 60 * 60 * 1000;

type ResourceTravelChainLink = {
    fromShift: IEvent;
    toShift: IEvent;
};

type ResourceTravelChain = {
    shifts: IEvent[];
    startShift: IEvent;
    endShift: IEvent;
    endMs: number;
    links: ResourceTravelChainLink[];
};

type ResourceShiftConnections = {
    connectedPreviousByShiftId: Map<string, string>;
    connectedNextByShiftId: Map<string, string>;
};

type TravelDuplicateDiagnostic = {
    duplicateType: 'id' | 'logical';
    keptShiftId: string;
    droppedShiftId: string;
    logicalKey: string;
};

type TravelSegmentSkipDiagnostic = {
    resourceId: string;
    travelKind: ITravelSegment['travelKind'];
    anchorShiftIds: string[];
    reason: 'missing-from' | 'missing-to' | 'same-coordinate' | 'invalid-window';
};

const isTravelDebugEnabled = (): boolean => {
    if (typeof globalThis === 'undefined') {
        return false;
    }

    try {
        const debugFlag = (globalThis as any).__RB_TRAVEL_DEBUG;
        if (debugFlag === true || debugFlag === 1 || debugFlag === '1') {
            return true;
        }

        const debugStorage = globalThis.localStorage?.getItem('__RB_TRAVEL_DEBUG')
            ?? globalThis.localStorage?.getItem('rb_travel_debug')
            ?? '';
        if (['1', 'true'].includes(String(debugStorage).trim().toLowerCase())) {
            return true;
        }

        const hostName = String((globalThis as any).location?.hostname ?? '').trim().toLowerCase();
        return hostName === 'localhost' || hostName === '127.0.0.1';
    } catch {
        return false;
    }
};

const logTravelDebug = (message: string, data?: unknown): void => {
    if (!isTravelDebugEnabled()) {
        return;
    }

    if (typeof data === 'undefined') {
        console.log(`[TravelDebug] ${message}`);
        return;
    }

    console.log(`[TravelDebug] ${message}`, data);
};

const getShiftSiteId = (shift: IEvent): string => String(shift.siteLocationDetails?.id ?? '').trim();

const getShiftDiagnosticSummary = (shift: IEvent) => ({
    id: String(shift.id ?? ''),
    bookingId: String(shift.BookingId ?? ''),
    resourceId: String(shift.resourceId ?? ''),
    title: String(shift.title ?? ''),
    start: new Date(shift.start).toISOString(),
    end: new Date(shift.end).toISOString(),
    siteLocationId: getShiftSiteId(shift),
    latitude: toFiniteCoordinateValue(shift.siteLocationDetails?.latitude),
    longitude: toFiniteCoordinateValue(shift.siteLocationDetails?.longitude),
});

const getTravelShiftLogicalKey = (shift: IEvent): string => {
    return [
        String(shift.resourceId ?? '').trim(),
        String(shift.BookingId ?? '').trim(),
        new Date(shift.start).toISOString(),
        new Date(shift.end).toISOString(),
        getShiftSiteId(shift),
        String(shift.title ?? '').trim().toLowerCase(),
        String(shift.description ?? '').trim().toLowerCase(),
        String(shift.workOrderType?.msdyn_workordertypeid ?? '').trim(),
        String(shift.serviceAccount?.accountId ?? '').trim(),
    ].join('|');
};

const scoreTravelShift = (shift: IEvent): number => {
    const shiftCoordinate = toCoordinateOrNull(shift.siteLocationDetails);
    let score = 0;

    if (String(shift.BookingId ?? '').trim()) {
        score += 4;
    }
    if (getShiftSiteId(shift)) {
        score += 2;
    }
    if (shiftCoordinate) {
        score += 2;
    }
    if (String(shift.description ?? '').trim()) {
        score += 1;
    }
    if (String(shift.title ?? '').trim()) {
        score += 1;
    }

    return score;
};

const choosePreferredTravelShift = (left: IEvent, right: IEvent): { kept: IEvent; dropped: IEvent; } => {
    const leftScore = scoreTravelShift(left);
    const rightScore = scoreTravelShift(right);
    if (rightScore > leftScore) {
        return { kept: right, dropped: left };
    }

    return { kept: left, dropped: right };
};

const normalizeResourceTravelShifts = (resourceShifts: IEvent[]): { shifts: IEvent[]; duplicates: TravelDuplicateDiagnostic[]; } => {
    const duplicates: TravelDuplicateDiagnostic[] = [];
    const uniqueById = new Map<string, IEvent>();

    resourceShifts.forEach((shift) => {
        const shiftId = String(shift.id ?? '').trim();
        if (!shiftId) {
            return;
        }

        const existing = uniqueById.get(shiftId);
        if (!existing) {
            uniqueById.set(shiftId, shift);
            return;
        }

        const choice = choosePreferredTravelShift(existing, shift);
        uniqueById.set(shiftId, choice.kept);
        duplicates.push({
            duplicateType: 'id',
            keptShiftId: String(choice.kept.id ?? ''),
            droppedShiftId: String(choice.dropped.id ?? ''),
            logicalKey: getTravelShiftLogicalKey(choice.kept),
        });
    });

    const uniqueByLogicalKey = new Map<string, IEvent>();
    Array.from(uniqueById.values()).forEach((shift) => {
        const logicalKey = getTravelShiftLogicalKey(shift);
        const existing = uniqueByLogicalKey.get(logicalKey);
        if (!existing) {
            uniqueByLogicalKey.set(logicalKey, shift);
            return;
        }

        const choice = choosePreferredTravelShift(existing, shift);
        uniqueByLogicalKey.set(logicalKey, choice.kept);
        duplicates.push({
            duplicateType: 'logical',
            keptShiftId: String(choice.kept.id ?? ''),
            droppedShiftId: String(choice.dropped.id ?? ''),
            logicalKey,
        });
    });

    return {
        shifts: Array.from(uniqueByLogicalKey.values()).sort(compareShiftTimeline),
        duplicates,
    };
};

const resolveShiftCoordinate = (
    shift: IEvent,
    siteLocationCoordinates?: ITravelCoordinate | null
): { latitude: number; longitude: number; } | null => {
    return toCoordinateOrNull(shift.siteLocationDetails) ?? toCoordinateOrNull(siteLocationCoordinates);
};

const compareShiftTimeline = (left: IEvent, right: IEvent): number => {
    const startDiff = new Date(left.start).getTime() - new Date(right.start).getTime();
    if (startDiff !== 0) {
        return startDiff;
    }

    const endDiff = new Date(right.end).getTime() - new Date(left.end).getTime();
    if (endDiff !== 0) {
        return endDiff;
    }

    return String(left.id ?? '').localeCompare(String(right.id ?? ''));
};

const isOvernightShift = (shift: IEvent): boolean => !isSameDay(new Date(shift.start), new Date(shift.end));

const createTravelChain = (shift: IEvent): ResourceTravelChain => ({
    shifts: [shift],
    startShift: shift,
    endShift: shift,
    endMs: new Date(shift.end).getTime(),
    links: [],
});

const shouldReturnHomeAfterShift = (currentShift: IEvent, nextShift: IEvent): boolean => {
    const nextShiftStartMs = new Date(nextShift.start).getTime();
    const currentShiftEndMs = new Date(currentShift.end).getTime();
    if (nextShiftStartMs <= currentShiftEndMs) {
        return false;
    }

    if (!isSameDay(new Date(currentShift.end), new Date(nextShift.start))) {
        return true;
    }

    const gapMs = nextShiftStartMs - currentShiftEndMs;
    return isOvernightShift(currentShift) && gapMs > RETURN_HOME_GAP_MS;
};

const buildResourceTravelChains = (resourceShifts: IEvent[]): ResourceTravelChain[] => {
    const travelChains: ResourceTravelChain[] = [];
    let currentChain: ResourceTravelChain | null = null;

    resourceShifts
        .slice()
        .sort(compareShiftTimeline)
        .forEach((shift) => {
            const shiftEndMs = new Date(shift.end).getTime();

            if (!currentChain) {
                currentChain = createTravelChain(shift);
                return;
            }

            if (new Date(shift.start).getTime() > currentChain.endMs && shouldReturnHomeAfterShift(currentChain.endShift, shift)) {
                travelChains.push(currentChain);
                currentChain = createTravelChain(shift);
                return;
            }

            if (new Date(shift.start).getTime() > currentChain.endMs) {
                currentChain.links.push({
                    fromShift: currentChain.endShift,
                    toShift: shift,
                });
            }

            currentChain.shifts.push(shift);
            if (shiftEndMs >= currentChain.endMs) {
                currentChain.endMs = shiftEndMs;
                currentChain.endShift = shift;
            }
        });

    if (currentChain) {
        travelChains.push(currentChain);
    }

    return travelChains;
};

const buildResourceShiftConnections = (resourceShifts: IEvent[]): ResourceShiftConnections => {
    const connectedPreviousByShiftId = new Map<string, string>();
    const connectedNextByShiftId = new Map<string, string>();
    const sortedShifts = resourceShifts.slice().sort(compareShiftTimeline);

    sortedShifts.forEach((shift, index) => {
        const shiftId = String(shift.id ?? '').trim();
        if (!shiftId) {
            return;
        }

        for (let previousIndex = index - 1; previousIndex >= 0; previousIndex -= 1) {
            const previousShift = sortedShifts[previousIndex];
            const previousShiftId = String(previousShift.id ?? '').trim();
            if (!previousShiftId) {
                continue;
            }

            const isConnectedToPrevious =
                new Date(shift.start).getTime() <= new Date(previousShift.end).getTime() ||
                !shouldReturnHomeAfterShift(previousShift, shift);

            if (isConnectedToPrevious) {
                connectedPreviousByShiftId.set(shiftId, previousShiftId);
                break;
            }

            if (shouldReturnHomeAfterShift(previousShift, shift)) {
                break;
            }
        }

        for (let nextIndex = index + 1; nextIndex < sortedShifts.length; nextIndex += 1) {
            const nextShift = sortedShifts[nextIndex];
            const nextShiftId = String(nextShift.id ?? '').trim();
            if (!nextShiftId) {
                continue;
            }

            const isConnectedToNext =
                new Date(nextShift.start).getTime() <= new Date(shift.end).getTime() ||
                !shouldReturnHomeAfterShift(shift, nextShift);

            if (isConnectedToNext) {
                connectedNextByShiftId.set(shiftId, nextShiftId);
                break;
            }

            if (shouldReturnHomeAfterShift(shift, nextShift)) {
                break;
            }
        }
    });

    return {
        connectedPreviousByShiftId,
        connectedNextByShiftId,
    };
};

const buildSegmentId = (
    resourceId: string,
    travelKind: ITravelSegment['travelKind'],
    anchorShiftIds: string[],
    windowStart: Date,
    windowEnd: Date
): string => {
    return [
        'travel',
        resourceId,
        travelKind,
        anchorShiftIds.join('_'),
        windowStart.toISOString(),
        windowEnd.toISOString()
    ].join(':');
};

const createSegment = (
    resourceId: string,
    travelKind: ITravelSegment['travelKind'],
    labelAlign: ITravelSegment['labelAlign'],
    from: { latitude: number; longitude: number; } | null,
    to: { latitude: number; longitude: number; } | null,
    windowStart: Date,
    windowEnd: Date,
    anchorShiftIds: string[],
    siteLocationDetails?: ITravelSegment['siteLocationDetails'],
    onSkip?: (diagnostic: TravelSegmentSkipDiagnostic) => void
): ITravelSegment | null => {
    if (!from) {
        onSkip?.({
            resourceId,
            travelKind,
            anchorShiftIds,
            reason: 'missing-from',
        });
        return null;
    }

    if (!to) {
        onSkip?.({
            resourceId,
            travelKind,
            anchorShiftIds,
            reason: 'missing-to',
        });
        return null;
    }

    if (coordinatesEqual(from, to)) {
        onSkip?.({
            resourceId,
            travelKind,
            anchorShiftIds,
            reason: 'same-coordinate',
        });
        return null;
    }

    if (windowEnd.getTime() <= windowStart.getTime()) {
        onSkip?.({
            resourceId,
            travelKind,
            anchorShiftIds,
            reason: 'invalid-window',
        });
        return null;
    }

    return {
        id: buildSegmentId(resourceId, travelKind, anchorShiftIds, windowStart, windowEnd),
        resourceId,
        from,
        to,
        windowStart,
        windowEnd,
        travelKind,
        labelAlign,
        anchorShiftIds,
        siteLocationDetails
    };
};

const buildTravelSegments = (
    resources: IResource[],
    shifts: IEvent[],
    siteLocationCoordinates?: ITravelCoordinate | null
): ITravelSegment[] => {
    const resourcesById = new Map<string, IResource>();
    resources.forEach((resource) => resourcesById.set(resource.id, resource));
    const segmentSkipDiagnostics: TravelSegmentSkipDiagnostic[] = [];

    const grouped = new Map<string, IEvent[]>();
    shifts
        .filter(isAssignedVisibleShift)
        .forEach((shift) => {
            const groupKey = String(shift.resourceId ?? '').trim();
            if (!groupKey) {
                return;
            }
            const existing = grouped.get(groupKey);
            if (existing) {
                existing.push(shift);
                return;
            }

            grouped.set(groupKey, [shift]);
        });

    const segments: ITravelSegment[] = [];

    grouped.forEach((resourceShifts, resourceId) => {
        const resource = resourcesById.get(resourceId);
        if (!resource || resourceShifts.length === 0) {
            return;
        }

        const { shifts: normalizedResourceShifts, duplicates } = normalizeResourceTravelShifts(resourceShifts);
        const shiftConnections = buildResourceShiftConnections(normalizedResourceShifts);
        const travelChains = buildResourceTravelChains(normalizedResourceShifts);
        const resourceSegments: ITravelSegment[] = [];

        if (duplicates.length > 0) {
            logTravelDebug('duplicate-shifts-detected', {
                resourceId,
                resourceName: resource.title,
                duplicates,
                rawShifts: resourceShifts.map(getShiftDiagnosticSummary),
                normalizedShifts: normalizedResourceShifts.map(getShiftDiagnosticSummary),
            });
        }

        const appendStartBaseSegment = (shift: IEvent) => {
            const shiftCoordinate = resolveShiftCoordinate(shift, siteLocationCoordinates);
            const startAnchor = resolveResourceTravelAnchor(
                resource,
                resource.startLocationType,
                resource.startLocationLabel,
                siteLocationCoordinates,
                shiftCoordinate
            );

            const startBaseSegment = createSegment(
                resourceId,
                'startBase',
                'start',
                startAnchor,
                shiftCoordinate,
                startOfDay(new Date(shift.start)),
                new Date(shift.start),
                [shift.id],
                shift.siteLocationDetails,
                (diagnostic) => segmentSkipDiagnostics.push(diagnostic)
            );

            if (startBaseSegment) {
                resourceSegments.push(startBaseSegment);
            }
        };

        const appendEndBaseSegment = (shift: IEvent) => {
            const shiftCoordinate = resolveShiftCoordinate(shift, siteLocationCoordinates);
            const endAnchor = resolveResourceTravelAnchor(
                resource,
                resource.endLocationType,
                resource.endLocationLabel,
                siteLocationCoordinates,
                shiftCoordinate
            );

            const endBaseSegment = createSegment(
                resourceId,
                'endBase',
                'end',
                shiftCoordinate,
                endAnchor,
                new Date(shift.end),
                endOfDay(new Date(shift.end)),
                [shift.id],
                shift.siteLocationDetails,
                (diagnostic) => segmentSkipDiagnostics.push(diagnostic)
            );

            if (endBaseSegment) {
                resourceSegments.push(endBaseSegment);
            }
        };

        travelChains.forEach((chain) => {
            appendStartBaseSegment(chain.startShift);

            for (const link of chain.links) {
                const currentShift = link.fromShift;
                const nextShift = link.toShift;
                const currentCoordinate = resolveShiftCoordinate(currentShift, siteLocationCoordinates);
                const nextCoordinate = resolveShiftCoordinate(nextShift, siteLocationCoordinates);

                const betweenSegment = createSegment(
                    resourceId,
                    'betweenShifts',
                    'start',
                    currentCoordinate,
                    nextCoordinate,
                    new Date(currentShift.end),
                    new Date(nextShift.start),
                    [currentShift.id, nextShift.id],
                    currentShift.siteLocationDetails,
                    (diagnostic) => segmentSkipDiagnostics.push(diagnostic)
                );

                if (betweenSegment) {
                    resourceSegments.push(betweenSegment);
                }
            }

            appendEndBaseSegment(chain.endShift);
        });

        resourceSegments
            .filter((segment) => {
                if (segment.travelKind === 'startBase') {
                    const anchorShiftId = String(segment.anchorShiftIds[0] ?? '').trim();
                    return !shiftConnections.connectedPreviousByShiftId.has(anchorShiftId);
                }

                if (segment.travelKind === 'endBase') {
                    const anchorShiftId = String(segment.anchorShiftIds[0] ?? '').trim();
                    return !shiftConnections.connectedNextByShiftId.has(anchorShiftId);
                }

                return true;
            })
            .forEach((segment) => {
                segments.push(segment);
            });

        if (isTravelDebugEnabled()) {
            console.groupCollapsed(`[TravelDebug] resource ${resource.title} (${resourceId})`);
            console.table(resourceShifts.map(getShiftDiagnosticSummary));
            console.table(normalizedResourceShifts.map(getShiftDiagnosticSummary));
            console.table(
                normalizedResourceShifts.map((shift) => ({
                    shiftId: String(shift.id ?? ''),
                    connectedPreviousShiftId: shiftConnections.connectedPreviousByShiftId.get(String(shift.id ?? '')) ?? '',
                    connectedNextShiftId: shiftConnections.connectedNextByShiftId.get(String(shift.id ?? '')) ?? '',
                }))
            );
            console.table(travelChains.map((chain, index) => ({
                chainIndex: index,
                startShiftId: String(chain.startShift.id ?? ''),
                endShiftId: String(chain.endShift.id ?? ''),
                start: new Date(chain.startShift.start).toISOString(),
                end: new Date(chain.endShift.end).toISOString(),
                shiftIds: chain.shifts.map((shift) => String(shift.id ?? '')).join(','),
                linkIds: chain.links.map((link) => `${String(link.fromShift.id ?? '')}->${String(link.toShift.id ?? '')}`).join(','),
            })));
            console.table(
                segments
                    .filter((segment) => segment.resourceId === resourceId)
                    .map((segment) => ({
                        travelKind: segment.travelKind,
                        anchorShiftIds: segment.anchorShiftIds.join(','),
                        windowStart: segment.windowStart.toISOString(),
                        windowEnd: segment.windowEnd.toISOString(),
                        from: `${segment.from.latitude},${segment.from.longitude}`,
                        to: `${segment.to.latitude},${segment.to.longitude}`,
                        siteLocationId: String(segment.siteLocationDetails?.id ?? ''),
                    }))
            );
            console.groupEnd();
        }
    });

    const uniqueSegments = Array.from(
        new Map(segments.map((segment) => ([
            [
                segment.resourceId,
                segment.travelKind,
                segment.from.latitude,
                segment.from.longitude,
                segment.to.latitude,
                segment.to.longitude,
                segment.windowStart.toISOString(),
                segment.windowEnd.toISOString(),
                segment.siteLocationDetails?.id ?? ''
            ].join('|'),
            segment
        ] as const))).values()
    );

    const sortedSegments = uniqueSegments.sort((left, right) => {
        if (left.resourceId !== right.resourceId) {
            return left.resourceId.localeCompare(right.resourceId);
        }
        return left.windowStart.getTime() - right.windowStart.getTime();
    });

    logTravelDebug('build-travel-segments-summary', {
        inputShiftCount: shifts.length,
        assignedVisibleShiftCount: shifts.filter(isAssignedVisibleShift).length,
        resourceCount: grouped.size,
        segmentCount: sortedSegments.length,
        skippedSegments: segmentSkipDiagnostics,
    });

    return sortedSegments;
};

const buildTravelMeasureRequest = (segments: ITravelSegment[]): ITravelMeasureRequest => {
    return {
        segments: segments.map((segment) => ({
            id: segment.id,
            from: segment.from,
            to: segment.to
        }))
    };
};

const formatTravelMetricLabel = (durationSeconds: number, distanceMeters: number): string => {
    const minutes = Math.max(1, Math.round(durationSeconds / 60));
    const kilometres = distanceMeters / 1000;
    return `${minutes}m • ${kilometres.toFixed(1)} km`;
};

const formatTravelLabel = (
    travelKind: ITravelSegment['travelKind'],
    durationSeconds: number,
    distanceMeters: number
): string => {
    const metricLabel = formatTravelMetricLabel(durationSeconds, distanceMeters);

    switch (travelKind) {
        case 'startBase':
            return `Home to Shift | ${metricLabel}`;
        case 'endBase':
            return `Shift to Home | ${metricLabel}`;
        case 'betweenShifts':
        default:
            return `Between Jobs | ${metricLabel}`;
    }
};

const createTravelEvent = (
    segment: ITravelSegment,
    measurement: ITravelMeasureResultSegment
): ITravelEvent | null => {
    const durationSeconds = Number(measurement.durationSeconds ?? 0);
    const distanceMeters = Number(measurement.distanceMeters ?? 0);

    if (!Number.isFinite(durationSeconds) || !Number.isFinite(distanceMeters) || durationSeconds <= 0 || distanceMeters < 0) {
        return null;
    }

    const windowStartMs = segment.windowStart.getTime();
    const windowEndMs = segment.windowEnd.getTime();
    const availableDurationMs = windowEndMs - windowStartMs;
    if (availableDurationMs <= 0) {
        return null;
    }

    const measuredDurationMs = Math.round(durationSeconds * 1000);
    let eventStartMs = windowStartMs;
    let eventEndMs = windowEndMs;
    let overflowMs = 0;

    if (segment.travelKind === 'startBase') {
        const actualStartMs = windowEndMs - measuredDurationMs;
        overflowMs = Math.max(0, windowStartMs - actualStartMs);
        eventStartMs = Math.max(windowStartMs, actualStartMs);
        eventEndMs = windowEndMs;
    } else {
        const actualEndMs = windowStartMs + measuredDurationMs;
        overflowMs = Math.max(0, actualEndMs - windowEndMs);
        eventStartMs = windowStartMs;
        eventEndMs = Math.min(windowEndMs, actualEndMs);
    }

    if (eventEndMs <= eventStartMs) {
        return null;
    }

    const overflowMinutes = overflowMs > 0 ? Math.ceil(overflowMs / 60000) : 0;
    const travelLabel = formatTravelLabel(segment.travelKind, durationSeconds, distanceMeters);
    const travelTooltip = overflowMinutes > 0
        ? `${travelLabel} (clipped by ${overflowMinutes}m)`
        : travelLabel;

    return {
        id: segment.id,
        resourceId: segment.resourceId,
        title: travelLabel,
        description: travelLabel,
        start: new Date(eventStartMs),
        end: new Date(eventEndMs),
        displayColor: undefined,
        shiftLength: Math.max(1, Math.round(durationSeconds / 60)),
        display: 'travel',
        editable: false,
        resourceEditable: false,
        travelKind: segment.travelKind,
        labelAlign: segment.labelAlign,
        durationSeconds,
        distanceMeters,
        overflowMinutes,
        anchorShiftIds: segment.anchorShiftIds,
        travelLabel,
        travelTooltip,
        siteLocationDetails: segment.siteLocationDetails
    };
};

const buildTravelEvents = (
    segments: ITravelSegment[],
    measurements: ITravelMeasureResultSegment[]
): ITravelEvent[] => {
    const measurementById = new Map<string, ITravelMeasureResultSegment>();
    measurements.forEach((measurement) => measurementById.set(measurement.id, measurement));

    const events = segments
        .map((segment) => {
            const measurement = measurementById.get(segment.id);
            if (!measurement || measurement.status !== 'ok') {
                return null;
            }

            return createTravelEvent(segment, measurement);
        })
        .filter((event): event is ITravelEvent => Boolean(event));

    logTravelDebug('build-travel-events-summary', {
        requestedSegmentCount: segments.length,
        measurementCount: measurements.length,
        okMeasurementCount: measurements.filter((measurement) => measurement.status === 'ok').length,
        missingMeasurements: segments
            .filter((segment) => !measurementById.has(segment.id))
            .map((segment) => segment.id),
        rejectedMeasurements: measurements
            .filter((measurement) => measurement.status !== 'ok')
            .map((measurement) => ({ id: measurement.id, status: measurement.status })),
        eventCount: events.length,
    });

    return events;
};

export {
    buildTravelEvents,
    buildTravelMeasureRequest,
    buildTravelSegments,
    formatTravelLabel,
    resolveResourceTravelAnchor
};
